import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalHeader from 'react-bootstrap/ModalHeader';
import ModalTitle from 'react-bootstrap/ModalTitle';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import Button from 'react-bootstrap/Button';
import config from '../../../config';

// SVM (South Vancouver Medical Clinic) branding - com.southvanmed is always SVM
const clinicName = 'South Vancouver Medical Clinic';
const websiteUrl = config.homepageUrl || 'SouthVanMed.com';
const serviceEmail = 'info@SouthVanMed.com';

// Modern styling for the modal (SVM blue theme)
const modalStyles = {
    modalHeader: {
        backgroundColor: '#f8f9fa',
        borderBottom: '3px solid #3498db',
        padding: '1rem 1.5rem'
    },
    headerTitle: {
        width: '100%',
        textAlign: 'center'
    },
    modalBody: {
        padding: '1.5rem',
        maxHeight: '70vh',
        overflowY: 'auto',
        fontSize: '0.95rem',
        lineHeight: '1.6'
    },
    modalFooter: {
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #dee2e6',
        padding: '1rem 1.5rem'
    },
    sectionTitle: {
        color: '#2c3e50',
        fontSize: '1.1rem',
        fontWeight: '600',
        marginTop: '1.5rem',
        marginBottom: '0.75rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #3498db'
    },
    paragraph: {
        marginBottom: '1rem',
        color: '#444',
        textAlign: 'justify'
    },
    emergencyBox: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
    },
    emergencyTitle: {
        color: '#856404',
        fontWeight: '700',
        marginBottom: '0.5rem'
    },
    list: {
        paddingLeft: '1.5rem',
        marginBottom: '1rem'
    },
    listItem: {
        marginBottom: '0.5rem',
        color: '#444'
    },
    versionInfo: {
        color: '#7f8c8d',
        fontSize: '0.9rem',
        marginBottom: '1rem'
    },
    link: {
        color: '#3498db',
        textDecoration: 'underline',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit'
    },
    contactBlock: {
        backgroundColor: '#e8f4fc',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem'
    }
};

const TermsModal = (props) => {
    return (
        <Modal
            show={props.show}
            onHide={e => props.footerClick(e, false)}
            size="lg"
            centered
            scrollable
            name="terms"
        >
            <ModalHeader closeButton style={modalStyles.modalHeader}>
                <ModalTitle style={modalStyles.headerTitle}>
                    <div style={{ fontSize: '1rem', color: '#7f8c8d' }}>{websiteUrl}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#2c3e50' }}>TERMS OF USE</div>
                </ModalTitle>
            </ModalHeader>

            <ModalBody style={modalStyles.modalBody}>
                <p style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    Please read this document completely. Continuing onto {websiteUrl} means that you agree with these Terms of Use.
                </p>

                <p style={modalStyles.versionInfo}>Last updated: January 20, 2026 – Version 3.0</p>

                <p style={modalStyles.paragraph}>
                    {clinicName} ("{clinicName}", "we", "us", or "our") owns and operates the website {websiteUrl}, including any subdomains (the "Website"). These Terms of Use (the "Terms"), together with our <button style={modalStyles.link} name="privacy-policy" onClick={e => props.footerClick(e, true, true, 'showTerms')}>Privacy Policy</button> and <button style={modalStyles.link} name="fee-schedule" onClick={e => props.footerClick(e, true, true, 'showTerms')}>Fee Schedule</button>, govern your access to and use of the Website and any associated patient portal (the "Portal"). By accessing the Website or Portal you acknowledge that you have read, understood, and agree to be bound by these Terms, the Privacy Policy, and the Fee Schedule. If you do not agree, you must stop using the Website and Portal immediately.
                </p>

                {/* Emergency Warning Box */}
                <div style={modalStyles.emergencyBox}>
                    <div style={modalStyles.emergencyTitle}>IMPORTANT – MEDICAL EMERGENCIES</div>
                    <p style={{ margin: 0, color: '#856404' }}>
                        The Website and Portal are not designed for medical emergencies. Do not use the Website or Portal to seek urgent medical care. If you are experiencing an urgent or life-threatening medical issue, call 9-1-1 immediately or attend the nearest emergency department or urgent care centre.
                    </p>
                </div>

                {/* Acceptance Section */}
                <h3 style={modalStyles.sectionTitle}>ACCEPTANCE AND CHANGES TO TERMS</h3>
                <p style={modalStyles.paragraph}>
                    These Terms form a legally binding electronic agreement between you and {clinicName}. We may update these Terms from time to time to reflect changes in our services, technology, or applicable laws in British Columbia and Canada. Updated Terms become effective when posted on the Website. We will make reasonable efforts to notify users of significant changes by email or website notice. Your continued use of the Website or Portal after changes are posted constitutes acceptance of the revised Terms.
                </p>

                {/* Permitted Use Section */}
                <h3 style={modalStyles.sectionTitle}>PERMITTED USE</h3>
                <p style={modalStyles.paragraph}>
                    You may use the Website and Portal solely for lawful, personal health-care related purposes. You agree not to:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>use the Website or Portal for any unlawful, fraudulent, abusive, or commercial purpose;</li>
                    <li style={modalStyles.listItem}>upload or transmit viruses, malware, or any harmful code;</li>
                    <li style={modalStyles.listItem}>send unsolicited advertising, spam, or mass communications;</li>
                    <li style={modalStyles.listItem}>collect information about other users without consent;</li>
                    <li style={modalStyles.listItem}>interfere with the operation or security of the Website or Portal;</li>
                    <li style={modalStyles.listItem}>attempt unauthorized access to any system or data;</li>
                    <li style={modalStyles.listItem}>use automated tools to scrape or copy content;</li>
                    <li style={modalStyles.listItem}>copy, modify, reverse engineer, or resell any part of the Website or Portal.</li>
                </ul>

                {/* The Patient Portal Section */}
                <h3 style={modalStyles.sectionTitle}>THE PATIENT PORTAL</h3>
                <p style={modalStyles.paragraph}>
                    The Portal is a secure online platform that allows patients to access certain services provided by physicians licensed in British Columbia (the "Services"), which may include appointment booking, messaging, forms, and payment processing.
                </p>
                <p style={modalStyles.paragraph}>
                    To use the Portal you must create a User Account and provide accurate identification and personal information. You agree to:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>keep your login credentials confidential;</li>
                    <li style={modalStyles.listItem}>notify us immediately of any suspected unauthorized access;</li>
                    <li style={modalStyles.listItem}>use the Portal only for your own personal health matters;</li>
                    <li style={modalStyles.listItem}>ensure all information you submit is true, current, and complete.</li>
                </ul>
                <p style={modalStyles.paragraph}>
                    We may suspend or terminate any User Account at our discretion for breach of these Terms or misuse of the Portal.
                </p>

                {/* Scope of Services Section */}
                <h3 style={modalStyles.sectionTitle}>SCOPE OF SERVICES</h3>
                <p style={modalStyles.paragraph}>
                    The Portal supports communication and administrative steps related to care. It does not replace an in-person medical assessment when clinically required and does not itself provide medical diagnosis. Clinical decisions remain at the discretion of the treating clinician in accordance with standards of the College of Physicians and Surgeons of British Columbia and applicable laws.
                </p>

                {/* Virtual Care Section */}
                <h3 style={modalStyles.sectionTitle}>VIRTUAL CARE SERVICES AND CONSENT</h3>
                <p style={modalStyles.paragraph}>
                    Where services are provided by telephone, video, secure messaging, or other electronic means ("Virtual Care"), you acknowledge and agree that:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>Virtual Care may not be appropriate for all medical conditions and does not replace in-person assessment when clinically required;</li>
                    <li style={modalStyles.listItem}>technology failures, poor connectivity, or privacy risks may occur despite reasonable safeguards;</li>
                    <li style={modalStyles.listItem}>clinicians may determine that an in-person visit is necessary for safe care;</li>
                    <li style={modalStyles.listItem}>you are responsible for attending Virtual Care sessions from a private and safe location and for protecting your own device security.</li>
                </ul>
                <p style={modalStyles.paragraph}>
                    By using the Portal or participating in Virtual Care you consent to receive assessment, advice, and treatment through electronic means in accordance with standards of the College of Physicians and Surgeons of British Columbia.
                </p>

                {/* Minors Section */}
                <h3 style={modalStyles.sectionTitle}>MINORS AND PARENT/GUARDIAN USE</h3>
                <p style={modalStyles.paragraph}>
                    If the patient is under 19 years of age, a parent or legal guardian must create or authorize the User Account unless the minor is legally capable of consenting to their own health care under British Columbia law. The parent or guardian represents and warrants that:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>they have legal authority to make decisions for the minor;</li>
                    <li style={modalStyles.listItem}>all information provided on behalf of the minor is accurate;</li>
                    <li style={modalStyles.listItem}>access to the Portal will be supervised in a manner that protects the minor's privacy.</li>
                </ul>
                <p style={modalStyles.paragraph}>
                    Where a minor is capable of consenting to their own medical treatment, access to information may be restricted in accordance with applicable privacy legislation and professional obligations.
                </p>

                {/* Email Communications Section */}
                <h3 style={modalStyles.sectionTitle}>EMAIL AND ELECTRONIC COMMUNICATION</h3>
                <p style={modalStyles.paragraph}>
                    {clinicName} may, from time to time, communicate with you via email with respect to our Services, and in so doing, may use some or all of the information you provide us with when registering for a User Account. The contents of those emails may include, but are not limited to, confirmations of appointments, laboratory and/or radiology requisitions, and brief notes regarding your medical condition. {clinicName} will take all reasonable steps to ensure that the transmission of any such information to you is done as securely as possible, and will only transmit as much information, including personally identifiable information, as is necessary for the purposes for which any such communications are made. You agree to allow {clinicName} to transmit any such information via email. If you do not agree to such transmission, please contact {clinicName}, and alternative arrangements may be made to allow you to retrieve this information.
                </p>

                {/* Contact Section */}
                <div style={modalStyles.contactBlock}>
                    <strong>Contact Us</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>
                        If you have any questions or concerns about these Terms, the Privacy Policy, or the Fee Schedule, you may contact us at <a href={`mailto:${serviceEmail}`} style={modalStyles.link}>{serviceEmail}</a>
                    </p>
                </div>
            </ModalBody>

            <ModalFooter style={modalStyles.modalFooter}>
                <Button
                    name="terms"
                    variant="primary"
                    onClick={e => props.footerClick(e, false)}
                    style={{ minWidth: '100px' }}
                >
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default TermsModal;
